import React from 'react';
import { tv } from 'tailwind-variants';
import { renderFormField } from 'helpers/FormFields/renderFormField';
import { CustomForm } from '.generated/Feature.Irwinmitchell.model';
import { Field as SitecoreField } from '@sitecore-content-sdk/nextjs';
import { evaluateConditions } from './utils/conditions';
import type {
  Condition,
  FormInstance,
  ShowIfCondition,
  StepConfig,
  FieldGroup as FieldGroupType,
  AddMoreConfig,
  CustomErrorMessages,
} from './types';
import FieldInfo from './FieldInfo';
import { InlineCardList } from 'helpers/WillsForm/WillsHelperComponents/InlineCard/InlineCardList';
import { RadioInlineCardList } from 'helpers/WillsForm/WillsHelperComponents/InlineCard/RadioInlineCardList';
import { EstateProgressBar } from './WillsHelperComponents/EstateProgressBar/EstateProgressBar';
import { findCardByGroupKey } from './utils/sections';
import { getStepFields } from './utils/steps';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';

type FieldType =
  | 'text'
  | 'phone'
  | 'select'
  | 'radio'
  | 'checkbox'
  | 'textarea'
  | 'email'
  | 'date'
  | 'info';

export interface FieldConfig {
  id: string;
  label: string;
  shortText?: string;
  type: FieldType;
  options?: { label: string; value: string; shortText?: string; showIf?: ShowIfCondition }[];
  placeholder?: string;
  helpText?: string;
  validation?: {
    required?: boolean;
    pattern?: string;
    min?: number;
    max?: number;
  };
  showIf?: Condition[];
  name?: string;
  displayname?: string;
  group?: string;
  minDate?: string;
  maxDate?: string;
  className?: string;
  hideLabel?: boolean;
  prefixText?: string | undefined;
  prefixIcon?: string | undefined;
  suffixIcon?: string | undefined;
  prefixIconAriaLabel?: string | undefined;
  suffixIconAriaLabel?: string | undefined;
  heading?: string;
  content?: string;
  disabled?: boolean;
  inlineCards?: {
    [optionValue: string]: {
      groupKeys: string[];
      showEdit?: boolean;
      showProgressBar?: boolean; // Show progress bar above inline cards
      progressBarType?: 'estate' | 'cash' | 'combined'; // Type of progress bar: 'estate' for percentage-based, 'cash' for amount-based, 'combined' for estate+cash together
      groupConfig?: {
        [groupKey: string]: {
          displayTitleFields?: string[];
          displayFields?: string[];
          editableFields?: string[];
          editFields?: FieldConfig[];
          showEdit?: boolean;
          showAddButton?: boolean; // Enable "Add new" button for this group
        };
      };
    };
  };
  /** Set by formFieldsMapper when localStorage willsFormDebugCmsSource='true' — which top-level props came from CMS */
  _cmsMappedKeys?: string[];
  /** Set by formFieldsMapper when localStorage willsFormDebugCmsSource='true' — which validation keys came from CMS */
  _cmsValidationKeys?: string[];
  /** Set by formFieldsMapper when localStorage willsFormDebugCmsSource='true' — actual values from CMS for mapped props */
  _cmsMappedValues?: Record<string, unknown>;
  /** Set by formFieldsMapper when localStorage willsFormDebugCmsSource='true' — actual values from CMS for validation */
  _cmsValidationValues?: Record<string, unknown>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FormValues = Record<string, any>;
export type FormErrors = Record<string, string | undefined>;
export type FormTouched = Record<string, boolean | undefined>;

/* ---------- Adapter function ---------- */

function convertFieldConfigToFormFieldItem(field: FieldConfig): CustomForm.FormField_Item | null {
  if (
    field.type !== 'radio' &&
    field.type !== 'text' &&
    field.type !== 'email' &&
    field.type !== 'phone' &&
    field.type !== 'select' &&
    field.type !== 'date' &&
    field.type !== 'textarea' &&
    field.type !== 'checkbox'
  )
    return null;

  const fieldName = field.name || field.id;
  if (!fieldName) return null;

  // Convert options to Sitecore Item format (for radio and select)
  const selectOptionsAndSection =
    field.type === 'radio' || field.type === 'select'
      ? (field.options || []).map((opt) => ({
          id: opt.value,
          name: opt.value,
          displayName: opt.label,
          fields: {
            value: { value: opt.value } as SitecoreField<string>,
            text: { value: opt.label } as SitecoreField<string>,
          },
        }))
      : [];

  return {
    id: field.id,
    name: fieldName,
    displayName: field.label,
    fields: {
      fieldName: { value: fieldName } as SitecoreField<string>,
      fieldId: { value: field.id } as SitecoreField<string>,
      fieldType: { value: field.type } as SitecoreField<string>,
      label: { value: field.label } as SitecoreField<string>,
      helpText: field.helpText ? ({ value: field.helpText } as SitecoreField<string>) : undefined,
      prefixText: field.prefixText
        ? ({ value: field.prefixText } as SitecoreField<string>)
        : undefined,
      prefixIcon: field.prefixIcon
        ? ({ value: field.prefixIcon } as SitecoreField<string>)
        : undefined,
      suffixIcon: field.suffixIcon
        ? ({ value: field.suffixIcon } as SitecoreField<string>)
        : undefined,
      prefixIconAriaLabel: field.prefixIconAriaLabel
        ? ({ value: field.prefixIconAriaLabel } as SitecoreField<string>)
        : undefined,
      suffixIconAriaLabel: field.suffixIconAriaLabel
        ? ({ value: field.suffixIconAriaLabel } as SitecoreField<string>)
        : undefined,
      placeholderText: field.placeholder
        ? ({ value: field.placeholder } as SitecoreField<string>)
        : undefined,
      required: field.validation?.required
        ? ({ value: true } as SitecoreField<boolean>)
        : ({ value: false } as SitecoreField<boolean>),
      disabled: { value: field.disabled ?? false } as SitecoreField<boolean>,
      minDate: field.minDate ? ({ value: field.minDate } as SitecoreField<string>) : undefined,
      maxDate: field.maxDate ? ({ value: field.maxDate } as SitecoreField<string>) : undefined,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      selectOptionsAndSection: selectOptionsAndSection as unknown as any,
      characterLimit: field.validation?.max
        ? ({ value: field.validation.max } as SitecoreField<number>)
        : undefined,
    },
  } as CustomForm.FormField_Item;
}

/* ---------- Field rendering helper ---------- */

const PERCENTAGE_FIELD_IDS = new Set([
  'childAmount',
  'charityPercentage',
  'businessOwnershipPercentage',
  'spousePartnerChildAmount',
]);

const shouldBlockPercentageInput = (rawValue: unknown): boolean => {
  if (typeof rawValue !== 'string') return false;
  const trimmed = rawValue.trim();
  if (trimmed === '') return false;
  const numericValue = Number(trimmed);
  if (Number.isNaN(numericValue)) return false;
  return numericValue > 100;
};

export const renderFieldComponent = (
  field: FieldConfig,
  values: FormValues,
  errors: FormErrors,
  touched: FormTouched,
  handleChange: (field: FieldConfig, rawValue: unknown) => void,
  setTouched: React.Dispatch<React.SetStateAction<FormTouched>>,
  completedForms?: Record<string, FormInstance[]>,
  stepsById?: Record<string, StepConfig>,
  onEditInstance?: (instanceId: string, groupKey: string) => void,
  // New props for radio inline cards
  setValues?: React.Dispatch<React.SetStateAction<FormValues>>,
  setCompletedForms?: React.Dispatch<React.SetStateAction<Record<string, FormInstance[]>>>,
  setErrors?: React.Dispatch<React.SetStateAction<FormErrors>>,
  localStorageKey?: string, // Optional localStorage key to load estate values from
  editingInstanceId?: string | null, // Optional instance ID being edited (for progress bar calculation)
  onNavigate?: (stepId: string) => void, // Navigation function for add new button
  customErrorMessages?: CustomErrorMessages
): React.ReactElement | null => {
  // Evaluate field-level showIf conditions
  if (!evaluateConditions(field.showIf, values)) {
    return null;
  }

  const value = values[field.id] ?? (field.type === 'checkbox' ? false : '');

  // Apply className to the wrapper div if provided
  const wrapperClassName = field.className || '';

  const showCmsSourceDebug =
    typeof window !== 'undefined' &&
    localStorage.getItem('willsFormDebugCmsSource') === 'true' &&
    (field._cmsMappedKeys?.length ?? 0) > 0;
  const cmsMappedKeys = field._cmsMappedKeys ?? [];
  const cmsValidationKeys = field._cmsValidationKeys ?? [];
  const cmsMappedValues = field._cmsMappedValues ?? {};
  const cmsValidationValues = field._cmsValidationValues ?? {};
  const configKeys = [
    'id',
    'label',
    'placeholder',
    'helpText',
    'options',
    'validation',
    'heading',
    'content',
  ].filter((k) => !cmsMappedKeys.includes(k));

  function formatCmsValue(val: unknown, maxLen = 60): string {
    if (val === undefined || val === null) return '—';
    if (typeof val === 'boolean') return val ? 'true' : 'false';
    if (typeof val === 'number') return String(val);
    if (Array.isArray(val)) {
      if (val.length === 0) return '[]';
      const first = val
        .slice(0, 2)
        .map((v) =>
          typeof v === 'object' && v && 'label' in v ? (v as { label: string }).label : String(v)
        );
      return `${val.length} option(s): ${first.join(', ')}${val.length > 2 ? '…' : ''}`;
    }
    if (typeof val === 'object' && val !== null) {
      const parts = Object.entries(val).map(([k, v]) => `${k}=${formatCmsValue(v, 15)}`);
      const s = parts.join(', ');
      return s.length > maxLen ? `${s.slice(0, maxLen)}…` : s;
    }
    const s = String(val);
    return s.length > maxLen ? `${s.slice(0, maxLen)}…` : s;
  }

  // Get hideLabel from field config, default to false if not specified
  const hideLabel = field.hideLabel ?? false;
  const prefixText = field.prefixText ?? '';
  const prefixIcon = field.prefixIcon ?? '';
  const suffixIcon = field.suffixIcon ?? '';
  const prefixIconAriaLabel = field.prefixIconAriaLabel ?? '';
  const suffixIconAriaLabel = field.suffixIconAriaLabel ?? '';

  const {
    inlineCardsWrapper,
    inlineCardsContainer,
    groupKeyContainer,
    checkboxLabel: _checkboxLabel,
    checkboxInput: _checkboxInput,
    requiredIndicator: _requiredIndicator,
    distributionEmptyState,
    distributionError,
    addBeneficiaryButtonContainer,
  } = TAILWIND_VARIANTS();

  const handleValueChange = (val: unknown) => {
    if (PERCENTAGE_FIELD_IDS.has(field.id) && shouldBlockPercentageInput(val)) {
      return;
    }
    handleChange(field, val);
  };

  return (
    <div key={field.id} className={wrapperClassName}>
      {showCmsSourceDebug && (
        <div
          className="mb-1 rounded border border-amber-400 bg-amber-50 px-2 py-1.5 text-xs text-amber-900"
          title={`Config (form.config.json): ${configKeys.join(', ') || 'none'}`}
        >
          <div className="font-bold">From CMS</div>
          {cmsMappedKeys.map((key) => (
            <div key={key} className="ml-1 mt-0.5">
              <span className="font-medium">{key}:</span>{' '}
              <span className="text-amber-800">{formatCmsValue(cmsMappedValues[key])}</span>
            </div>
          ))}
          {cmsValidationKeys.length > 0 && (
            <>
              <div className="mt-1 font-bold">Validation from CMS</div>
              {cmsValidationKeys.map((key) => (
                <div key={key} className="ml-1 mt-0.5">
                  <span className="font-medium">{key}:</span>{' '}
                  <span className="text-amber-800">{formatCmsValue(cmsValidationValues[key])}</span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
      {field.type === 'text' &&
        (() => {
          const formFieldItem = convertFieldConfigToFormFieldItem(field);
          return formFieldItem ? (
            <div>
              {renderFormField(
                formFieldItem,
                errors,
                touched,
                undefined,
                String(value ?? ''),
                handleValueChange,
                () => setTouched((prev) => ({ ...prev, [field.id]: true })),
                {
                  hideLabel,
                  isStandalone: true,
                  prefixText: prefixText,
                  prefixIcon: prefixIcon,
                  suffixIcon: suffixIcon,
                  prefixIconAriaLabel: prefixIconAriaLabel,
                  suffixIconAriaLabel: suffixIconAriaLabel,
                }
              )}
            </div>
          ) : null;
        })()}

      {field.type === 'email' &&
        (() => {
          const formFieldItem = convertFieldConfigToFormFieldItem(field);
          return formFieldItem ? (
            <div>
              {renderFormField(
                formFieldItem,
                errors,
                touched,
                undefined,
                String(value ?? ''),
                handleValueChange,
                () => setTouched((prev) => ({ ...prev, [field.id]: true })),
                { hideLabel, isStandalone: true }
              )}
            </div>
          ) : null;
        })()}

      {field.type === 'textarea' &&
        (() => {
          const formFieldItem = convertFieldConfigToFormFieldItem(field);
          return formFieldItem ? (
            <div>
              {renderFormField(
                formFieldItem,
                errors,
                touched,
                undefined,
                String(value ?? ''),
                handleValueChange,
                () => setTouched((prev) => ({ ...prev, [field.id]: true })),
                { hideLabel, isStandalone: true }
              )}
            </div>
          ) : null;
        })()}

      {field.type === 'select' &&
        (() => {
          const formFieldItem = convertFieldConfigToFormFieldItem(field);
          return formFieldItem ? (
            <div>
              {renderFormField(
                formFieldItem,
                errors,
                touched,
                undefined,
                String(value ?? ''),
                handleValueChange,
                () => setTouched((prev) => ({ ...prev, [field.id]: true })),
                { hideLabel, isStandalone: true }
              )}
            </div>
          ) : null;
        })()}

      {field.type === 'radio' &&
        (() => {
          // Filter options by showIf so only visible options are rendered
          const visibleOptions = (field.options || []).filter((opt) =>
            evaluateConditions(opt.showIf, values)
          );
          const fieldWithVisibleOptions = { ...field, options: visibleOptions };
          const formFieldItem = convertFieldConfigToFormFieldItem(fieldWithVisibleOptions);
          const currentValue = value as string;
          return formFieldItem ? (
            <div>
              {renderFormField(
                formFieldItem,
                errors,
                touched,
                undefined,
                value,
                (val) => handleChange(field, val),
                () => setTouched((prev) => ({ ...prev, [field.id]: true })),
                { hideLabel, isStandalone: true }
              )}
              {/* Render inline cards between radio options if configured */}
              {field.inlineCards && visibleOptions.length > 0 && completedForms && stepsById && (
                <div className={inlineCardsWrapper()}>
                  {visibleOptions.map((option, index) => {
                    const cardConfig = field.inlineCards?.[option.value];
                    if (!cardConfig || !cardConfig.groupKeys) return null;

                    // Show cards only if this option is selected
                    const isSelected = currentValue === option.value;
                    if (!isSelected) return null;

                    const totalBeneficiaryCount = (cardConfig.groupKeys || []).reduce(
                      (sum, key) => sum + (completedForms?.[key]?.length ?? 0),
                      0
                    );
                    const isDistributionStepWithNoBeneficiaries =
                      field.id === 'estateDistributionPreference' && totalBeneficiaryCount === 0;

                    // Check if progress bar should be shown
                    const showProgressBar = cardConfig.showProgressBar === true;
                    const progressBarType = cardConfig.progressBarType || 'estate';

                    return (
                      <div
                        key={`inline-cards-${option.value}-${index}`}
                        className={inlineCardsContainer()}
                      >
                        {/* Estate Progress Bar - show above inline cards if configured */}
                        {showProgressBar &&
                          (() => {
                            // Extract current form values that might be being edited for progress bar
                            const currentFormValuesForProgressBar: FormValues = {};
                            if (values.charityPercentage !== undefined) {
                              currentFormValuesForProgressBar.charityPercentage =
                                values.charityPercentage;
                            }
                            if (values.cashGiftAmount !== undefined) {
                              currentFormValuesForProgressBar.cashGiftAmount =
                                values.cashGiftAmount;
                            }
                            if (values.cashGiftToSomeoneAmount !== undefined) {
                              currentFormValuesForProgressBar.cashGiftToSomeoneAmount =
                                values.cashGiftToSomeoneAmount;
                            }
                            if (values.childAmount !== undefined) {
                              currentFormValuesForProgressBar.childAmount = values.childAmount;
                            }
                            if (values.spousePartnerChildAmount !== undefined) {
                              currentFormValuesForProgressBar.spousePartnerChildAmount =
                                values.spousePartnerChildAmount;
                            }

                            return (
                              <EstateProgressBar
                                values={values}
                                completedForms={completedForms || {}}
                                addMoreConfig={{
                                  fieldIds: [],
                                  groupKey: '', // Not used for progress bar calculation
                                  instancePrefix: '',
                                  cardTitle: '',
                                  showProgressBar: true,
                                  progressBarType: progressBarType,
                                }}
                                currentFormValues={currentFormValuesForProgressBar}
                                localStorageKey={localStorageKey}
                                editingInstanceId={editingInstanceId}
                              />
                            );
                          })()}
                        {cardConfig.groupKeys.map((groupKey) => {
                          const instances = completedForms[groupKey] || [];
                          // Check if showAddButton is configured - if so, render even with no instances
                          const showAddButton =
                            cardConfig.groupConfig?.[groupKey]?.showAddButton ?? false;
                          if (instances.length === 0 && !showAddButton) return null;

                          // First, try to find a step with addMoreConfig (for addMore groups)
                          let step = Object.values(stepsById).find(
                            (s) => s.addMoreConfig?.groupKey === groupKey
                          );

                          let fieldGroup: FieldGroupType | null = null;
                          let cardTitle: string | undefined;
                          let displayTitleFields: string[] | undefined;
                          let fieldIds: string[] = [];
                          let instancePrefix: string | undefined;
                          let addMoreConfig: AddMoreConfig | undefined;

                          if (step && step.addMoreConfig) {
                            // Handle addMore groups
                            addMoreConfig = step.addMoreConfig;
                            cardTitle = step.addMoreConfig.cardTitle;
                            displayTitleFields = step.addMoreConfig.displayTitleFields;
                            fieldIds = step.addMoreConfig.fieldIds || [];
                            instancePrefix = step.addMoreConfig.instancePrefix;

                            // Find the fieldGroup that contains the addMore fields
                            if (step.addMoreConfig.fieldGroupIndex !== undefined) {
                              fieldGroup =
                                step.fieldGroups?.[step.addMoreConfig.fieldGroupIndex] || null;
                            }

                            if (!fieldGroup && step.addMoreConfig.fieldIds && step.fieldGroups) {
                              fieldGroup =
                                step.fieldGroups.find((fg) => {
                                  if (!fg.fields) return false;
                                  const fieldIdsInGroup = fg.fields.map((f) => f.id);
                                  return step?.addMoreConfig?.fieldIds?.some((fieldId) =>
                                    fieldIdsInGroup.includes(fieldId)
                                  );
                                }) || null;
                            }
                          } else {
                            // Handle regular card groups
                            const cardResult = findCardByGroupKey(
                              groupKey,
                              Object.values(stepsById)
                            );
                            if (!cardResult) return null;

                            const { card } = cardResult;
                            cardTitle = card.title;
                            displayTitleFields = card.displayGrouping
                              ? card.displayGrouping.flatMap((g) => g.combineFields)
                              : undefined;

                            // Get steps for this card
                            const cardSteps = card.stepIds
                              .map((stepId) => stepsById?.[stepId])
                              .filter((s) => s !== undefined) as StepConfig[];

                            if (cardSteps.length === 0) return null;

                            // Use the first step (or find step with matching field)
                            step = cardSteps[0];

                            // Create a fieldGroup based on displayFields from groupConfig
                            const displayFields = cardConfig.groupConfig?.[groupKey]?.displayFields;
                            if (displayFields && displayFields.length > 0) {
                              // Find fields from card steps that match displayFields
                              const allFields: FieldConfig[] = [];
                              cardSteps.forEach((cardStep) => {
                                const stepFields = getStepFields(cardStep);
                                stepFields.forEach((field) => {
                                  // Apply card's includeFieldIds/excludeFieldIds filters
                                  if (
                                    card.excludeFieldIds &&
                                    card.excludeFieldIds.includes(field.id)
                                  ) {
                                    return;
                                  }
                                  if (
                                    card.includeFieldIds &&
                                    !card.includeFieldIds.includes(field.id)
                                  ) {
                                    return;
                                  }
                                  // Only include if in displayFields
                                  if (displayFields.includes(field.id)) {
                                    allFields.push(field);
                                  }
                                });
                              });

                              if (allFields.length > 0) {
                                fieldGroup = {
                                  className: '',
                                  fields: allFields,
                                };
                              }
                            } else {
                              // If no displayFields specified, use all fields from first step
                              const stepFields = getStepFields(step);
                              const filteredFields = stepFields.filter((field) => {
                                if (
                                  card.excludeFieldIds &&
                                  card.excludeFieldIds.includes(field.id)
                                ) {
                                  return false;
                                }
                                if (
                                  card.includeFieldIds &&
                                  !card.includeFieldIds.includes(field.id)
                                ) {
                                  return false;
                                }
                                return true;
                              });
                              fieldGroup = {
                                className: '',
                                fields: filteredFields,
                              };
                            }

                            // Collect fieldIds from the fieldGroup
                            fieldIds = fieldGroup?.fields.map((f: FieldConfig) => f.id) || [];
                          }

                          if (!fieldGroup || !step) return null;

                          // Respect card maxInstances for Add button (e.g. executors list)
                          const cardForLimit = findCardByGroupKey(
                            groupKey,
                            Object.values(stepsById)
                          );
                          const canAddMore =
                            !cardForLimit ||
                            cardForLimit.card.maxInstances == null ||
                            instances.length < cardForLimit.card.maxInstances;

                          // Use RadioInlineCardList if we have the required props, otherwise fallback to InlineCardList
                          const hasRequiredProps = setValues && setCompletedForms && setErrors;

                          return (
                            <div key={groupKey} className={groupKeyContainer()}>
                              {hasRequiredProps ? (
                                <RadioInlineCardList
                                  instances={instances}
                                  steps={[
                                    {
                                      id: step.id,
                                      title: step.title,
                                      fieldGroups: [fieldGroup],
                                    },
                                  ]}
                                  title={cardTitle || 'Card'}
                                  fieldGroup={fieldGroup}
                                  values={values}
                                  errors={errors}
                                  touched={touched}
                                  handleChange={handleChange}
                                  setTouched={setTouched}
                                  setValues={setValues}
                                  setCompletedForms={setCompletedForms}
                                  setErrors={setErrors}
                                  groupKey={groupKey}
                                  fieldIds={fieldIds}
                                  displayTitleFields={
                                    cardConfig.groupConfig?.[groupKey]?.displayTitleFields ||
                                    displayTitleFields
                                  }
                                  displayFields={cardConfig.groupConfig?.[groupKey]?.displayFields}
                                  editableFields={
                                    cardConfig.groupConfig?.[groupKey]?.editableFields
                                  }
                                  editFields={cardConfig.groupConfig?.[groupKey]?.editFields}
                                  showEdit={
                                    cardConfig.groupConfig?.[groupKey]?.showEdit ??
                                    cardConfig.showEdit ??
                                    true
                                  }
                                  showAddButton={
                                    cardConfig.groupConfig?.[groupKey]?.showAddButton ?? false
                                  }
                                  canAddMore={canAddMore}
                                  instancePrefix={instancePrefix}
                                  cardTitle={cardTitle}
                                  isSubmitting={false}
                                  completedForms={completedForms}
                                  addMoreConfig={addMoreConfig}
                                  localStorageKey={localStorageKey}
                                  onNavigate={onNavigate}
                                  addMoreStepId={step && step.addMoreConfig ? step.id : undefined}
                                  isDistributionStepWithNoBeneficiaries={
                                    isDistributionStepWithNoBeneficiaries
                                  }
                                  customErrorMessages={customErrorMessages}
                                />
                              ) : (
                                <InlineCardList
                                  instances={instances}
                                  steps={[
                                    {
                                      id: step.id,
                                      title: step.title,
                                      fieldGroups: [fieldGroup],
                                    },
                                  ]}
                                  title={cardTitle || 'Card'}
                                  onEdit={(instanceId) => {
                                    const groupConfig = cardConfig.groupConfig?.[groupKey];
                                    const canEdit =
                                      groupConfig?.showEdit !== false &&
                                      cardConfig.showEdit !== false;

                                    if (onEditInstance && canEdit) {
                                      onEditInstance(instanceId, groupKey);
                                    }
                                  }}
                                  editingInstanceId={undefined}
                                  fieldGroup={fieldGroup}
                                  values={values}
                                  errors={errors}
                                  touched={touched}
                                  handleChange={handleChange}
                                  setTouched={setTouched}
                                  onUpdate={() => {}}
                                  isSubmitting={false}
                                  canAddMore={false}
                                  displayTitleFields={displayTitleFields}
                                  displayFields={cardConfig.groupConfig?.[groupKey]?.displayFields}
                                  editableFields={
                                    cardConfig.groupConfig?.[groupKey]?.editableFields
                                  }
                                  editFields={cardConfig.groupConfig?.[groupKey]?.editFields}
                                  showEdit={
                                    cardConfig.groupConfig?.[groupKey]?.showEdit ??
                                    cardConfig.showEdit ??
                                    true
                                  }
                                />
                              )}
                            </div>
                          );
                        })}
                        {/* Empty state: error + Add beneficiary CTA when no beneficiaries on distribution step */}
                        {isDistributionStepWithNoBeneficiaries && (
                          <div className={distributionEmptyState()}>
                            {errors.estateDistributionPreference && (
                              <p className={distributionError()} role="alert">
                                {errors.estateDistributionPreference}
                              </p>
                            )}
                            {onNavigate && (
                              <div className={addBeneficiaryButtonContainer()}>
                                <ButtonWrapper
                                  type="button"
                                  text="Add beneficiary"
                                  ctaComponentClass="default"
                                  ctaVariant="primary"
                                  onClick={() =>
                                    onNavigate('step-8-1-estate-beneficiaries-charities')
                                  }
                                  remixIcon="add-line"
                                  remixIconSize="sm"
                                  ctaIconAlignment="right"
                                  className="w-fit leading-[100%] md:leading-[100%] lg:leading-[100%] h-12"
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : null;
        })()}

      {field.type === 'date' &&
        (() => {
          const formFieldItem = convertFieldConfigToFormFieldItem(field);
          return formFieldItem ? (
            <div>
              {renderFormField(
                formFieldItem,
                errors,
                touched,
                undefined,
                String(value ?? ''),
                handleValueChange,
                () => setTouched((prev) => ({ ...prev, [field.id]: true })),
                { hideLabel, isStandalone: true }
              )}
            </div>
          ) : null;
        })()}

      {field.type === 'checkbox' &&
        (() => {
          const formFieldItem = convertFieldConfigToFormFieldItem(field);
          return formFieldItem ? (
            <div>
              {renderFormField(
                formFieldItem,
                errors,
                touched,
                undefined,
                String(value ?? ''),
                handleValueChange,
                () => setTouched((prev) => ({ ...prev, [field.id]: true })),
                { hideLabel, isStandalone: true }
              )}
            </div>
          ) : null;
        })()}

      {field.type === 'info' && <FieldInfo heading={field?.heading} content={field?.content} />}
    </div>
  );
};

const TAILWIND_VARIANTS = tv({
  slots: {
    inlineCardsWrapper: ['mt-spacing-spacing-24'],
    inlineCardsContainer: [
      'sub-section-container',
      'flex',
      'flex-col',
      'gap-spacing-spacing-16',
      'mt-spacing-spacing-16',
    ],
    groupKeyContainer: ['w-full'],
    checkboxLabel: ['flex', 'items-center', 'text-sm'],
    checkboxInput: ['mr-2'],
    requiredIndicator: ['text-red-500', 'ml-1'],
    distributionEmptyState: ['flex', 'flex-col', 'gap-spacing-spacing-16', 'mt-spacing-spacing-16'],
    distributionError: ['text-component-form-error', 'font-typography-font-family-body', 'text-sm'],
    addBeneficiaryButtonContainer: ['w-full'],
  },
});
